/**
 * Доля хвоста публичного рендерера: вид `slider_range` (ползунок).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с управляемым полем `SliderRangeInput` и округлением к шагу `snapToSliderStep`. Метка `case 'slider_range'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderSliderRange(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { useFormValues } from '../FormValuesContext';
import { blockTextRoles } from '../semanticTextRoles';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';


/**
 * Управляемый ползунок.
 *
 * WEB-1336 (П4) сделал блок управляемым и посчитал НАСТОЯЩУЮ середину:
 * формула старта без скобок `min ?? 0 + max` вычислялась как
 * `min ?? (0 + max)` — при min=20/max=100 старт был 10 (ниже минимума,
 * браузер прижимал бегунок к 20 и «середины» не существовало никогда).
 * Середина ещё и округляется к шагу (`snapToSliderStep`, десятка 4 п.6а):
 * при sliderStep=5 сырая середина могла оказаться значением, недостижимым
 * самим бегунком, и записанный ответ расходился с тем, что видно на экране.
 * Обе эти половины ОСТАЮТСЯ — они про ПОКАЗ.
 *
 * WEB-1741 развернул третью половину WEB-1336 — засев ответа. Тогда блок стал
 * писать середину в FormValues прямо при отрисовке, чтобы «согласие со
 * стартовым значением» не терялось. Цена оказалась выше выгоды:
 *   (а) середина уезжала в аналитику КАК ОТВЕТ (ползунок входит в
 *       ANSWER_BLOCK_TYPES) — «50» осознанное и «50» от человека, который
 *       проскочил экран не глядя, в выгрузке неотличимы, и распределение
 *       получало пик по центру, которого в реальности нет;
 *   (б) тумблер «Обязательное» на ползунке не делал НИЧЕГО: гейт
 *       (quiz-runtime/requiredGate) считает заполненным непустое значение по
 *       block.id, а оно лежало там всегда — экран пропускал вперёд человека,
 *       который вопроса не читал.
 *
 * Теперь ПОКАЗ и ОТВЕТ разведены при одной точке правды: бегунок стоит на
 * середине за счёт запасного значения в render, а в FormValues попадает
 * только то, что покупатель выставил сам. Наличие значения по block.id =
 * «покупатель выбрал» — ровно то, что читают и отчёт, и гейт. Молчание
 * честнее выдумки: ветка логики на нетронутом ползунке просто не совпадёт
 * (движок логики fail-open), а кому ответ обязателен — включит «Обязательное».
 *
 * Про RESTART (десятка 4 п.6б): после resetValues() значение снова undefined,
 * и поле обязано остаться controlled. За это отвечает `current` ниже
 * (`chosenValue ?? midpoint`), а НЕ эффект — сеющего эффекта больше нет.
 */
function snapToSliderStep(value: number, min: number, step: number): number {
  if (!step || step <= 0) return value;
  return min + Math.round((value - min) / step) * step;
}

function SliderRangeInput({
  block,
}: {
  block: EditorBlock;
}): React.JSX.Element {
  const { values, setValue } = useFormValues();
  const min = block.sliderMin ?? 0;
  const max = block.sliderMax ?? 100;
  const step = block.sliderStep ?? 1;
  const midpoint = String(
    snapToSliderStep(Math.round((min + max) / 2), min, step),
  );
  // Ответ покупателя, если он его дал. undefined = не давал — так это и
  // видят отчёт и гейт «Обязательное».
  const chosenValue = values[block.id];
  // Что показать бегунку. Середина здесь — ВИД по умолчанию, а не ответ:
  // в FormValues она не попадает, пока покупатель не подвинет бегунок сам.
  // Заодно держит поле controlled после resetValues() (RESTART воронки).
  const current = chosenValue ?? midpoint;

  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={current}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        setValue(block.id, e.target.value)
      }
      data-testid={`public-input-${block.id}`}
      style={{ width: '100%' }}
    />
  );
}

export function renderSliderRange(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, interp } = ctx;
  // WEB-1692 юнит 4: владелец назвал ровно две сущности — подпись над
  // ползунком и числа под ним. Общие поля обёртки красили и меряли их
  // РАЗОМ, поэтому разведены двумя ролями; своё поле роли сильнее
  // унаследованного от обёртки (явный размер старше наследуемого).
  const sliderRoles = blockTextRoles(block, 'slider_range');
  return (
    <div style={{ marginBottom: 16 }}>
      {block.content && (
        <label
          data-testid="slider-range-label"
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 12,
            color: 'var(--color-text-primary, inherit)',
            ...sliderRoles.title,
          }}
        >
          {interp(block.content)}
        </label>
      )}
      <SliderRangeInput block={block} />
      <div
        data-testid="slider-range-bounds"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 13,
          color: 'var(--color-text-secondary, #8c8c8c)',
          marginTop: 4,
          ...sliderRoles.secondary,
        }}
      >
        <span>{block.sliderMin ?? 0}</span>
        <span>{block.sliderMax ?? 100}</span>
      </div>
    </div>
  );
}
